/**
 * HTTP Client
 * Lightweight fetch wrapper with type safety and error handling
 */

import { HttpError } from '@/api/http/errors'
import { getApiBaseUrl } from '@/config/config'
import { logger } from '@/utils/logger'
import { isDebugEnabled } from '@/config/config'
import { getStoredConnectionId } from '@/providers/websocket'
import {
  exchangeSessionForJwt,
  getStoredAuthToken,
  getStoredJwtToken,
  refreshJwtToken,
  triggerForceLogout,
} from '@/api/services/auth.service'

function safeSetHeader(headers: Headers, name: string, value: string): boolean {
  try {
    headers.set(name, value)
    return true
  } catch {
    // Some headers (e.g. "Connection") are forbidden in browsers and will throw.
    return false
  }
}

function createAbortError(message = 'The operation was aborted'): Error {
  const err = new Error(message)

  err.name = 'AbortError'
  return err
}

async function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return
  if (signal?.aborted) throw createAbortError()

  await new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup()
      resolve()
    }, ms)

    const onAbort = () => {
      cleanup()
      reject(createAbortError())
    }

    const cleanup = () => {
      clearTimeout(timeoutId)
      signal?.removeEventListener('abort', onAbort)
    }

    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

async function waitForJwtToken(options?: {
  timeoutMs?: number
  pollIntervalMs?: number
  signal?: AbortSignal
}): Promise<string> {
  const timeoutMs = options?.timeoutMs ?? 5000
  const pollIntervalMs = options?.pollIntervalMs ?? 50
  const signal = options?.signal

  const start = Date.now()

  // Fast path
  const initial = getStoredJwtToken()

  if (initial) return initial

  while (Date.now() - start < timeoutMs) {
    if (signal?.aborted) throw createAbortError()

    await sleep(pollIntervalMs, signal)

    const token = getStoredJwtToken()

    if (token) return token

    // If we have the wpcauth session id (wpcid) but no JWT yet, exchange it.
    const sessionId = getStoredAuthToken()

    if (sessionId) {
      return await exchangeSessionForJwt(sessionId)
    }
  }

  throw new Error(
    'Missing authentication token; cannot set required JWT `Authorization` header. Please login again.'
  )
}

async function waitForConnectionId(options?: {
  timeoutMs?: number
  pollIntervalMs?: number
  signal?: AbortSignal
}): Promise<string> {
  const timeoutMs = options?.timeoutMs ?? 10000
  const pollIntervalMs = options?.pollIntervalMs ?? 50
  const signal = options?.signal

  const start = Date.now()

  // Fast path
  const initial = getStoredConnectionId()

  if (initial) return initial

  while (Date.now() - start < timeoutMs) {
    if (signal?.aborted) throw createAbortError()

    await sleep(pollIntervalMs, signal)

    const id = getStoredConnectionId()

    if (id) return id
  }

  throw new Error(
    'Missing websocket connectionId; cannot set required `X-Connection-Id` header. Wait for websocket auth to complete and try again.'
  )
}

/**
 * Request configuration (extends RequestInit with custom options)
 */
interface RequestConfig extends Omit<RequestInit, 'body'> {
  params?: Record<string, string | number | boolean | null | undefined>
  body?: unknown
  /**
   * Skip attaching auth header for this request.
   * (Used for endpoints that don't require JWT)
   */
  skipAuth?: boolean
  /**
   * Skip refresh-on-401 logic for this request.
   */
  skipAuthRefresh?: boolean
  /**
   * Skip attaching the websocket connection id headers for this request.
   */
  skipConnectionId?: boolean
  /**
   * Abort the request after this many milliseconds.
   * Combined with any caller-provided `signal`.
   */
  timeoutMs?: number
}

/**
 * HTTP client configuration
 */
interface HttpClientConfig {
  baseURL?: string
  defaultHeaders?: HeadersInit
}

/**
 * HTTP Client class
 */
export class HttpClient {
  private baseURL: string
  private defaultHeaders: HeadersInit

  constructor(config: HttpClientConfig = {}) {
    this.baseURL = config.baseURL || getApiBaseUrl()
    this.defaultHeaders = config.defaultHeaders || {
      'Content-Type': 'application/json',
    }
  }

  /**
   * Build full URL from base URL and path
   */
  private buildURL(path: string, params?: RequestConfig['params']): string {
    const url = new URL(path, this.baseURL)

    // Add query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  /**
   * Merge headers
   */
  private mergeHeaders(defaultHeaders: HeadersInit, configHeaders?: HeadersInit): Headers {
    const headers = new Headers(defaultHeaders)

    if (configHeaders) {
      if (configHeaders instanceof Headers) {
        configHeaders.forEach((value, key) => {
          headers.set(key, value)
        })
      } else if (Array.isArray(configHeaders)) {
        configHeaders.forEach(([key, value]) => {
          headers.set(key, value)
        })
      } else {
        Object.entries(configHeaders).forEach(([key, value]) => {
          headers.set(key, value)
        })
      }
    }

    return headers
  }

  /**
   * Execute HTTP request
   */
  private async executeRequest<T>(url: string, config: RequestConfig): Promise<T> {
    // Merge headers
    const headers = this.mergeHeaders(this.defaultHeaders, config.headers)

    const isAuthEndpoint = url.includes('/authentication/')
    const shouldAttachAuth = !config.skipAuth && !isAuthEndpoint

    const shouldAttachConnectionId = !config.skipConnectionId && !isAuthEndpoint

    const timeoutController =
      typeof config.timeoutMs === 'number' && config.timeoutMs > 0 ? new AbortController() : null
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (timeoutController) {
      timeoutId = setTimeout(() => {
        timeoutController.abort()
      }, config.timeoutMs)
    }

    const callerSignal = config.signal ?? undefined
    const onCallerAbort = () => {
      timeoutController?.abort()
    }

    if (callerSignal && timeoutController) {
      if (callerSignal.aborted) {
        timeoutController.abort()
      } else {
        callerSignal.addEventListener('abort', onCallerAbort, { once: true })
      }
    }

    const requestSignal = timeoutController?.signal ?? callerSignal

    try {
      if (shouldAttachConnectionId) {
        const connectionId = await waitForConnectionId({ signal: requestSignal })

        const ok = safeSetHeader(headers, 'X-Connection-Id', connectionId)

        if (!ok && isDebugEnabled()) {
          logger.warn(
            'Browser blocked setting forbidden header "Connection". If the API requires it, you will need a server-side proxy or the backend must accept a custom header like "X-Connection-Id".'
          )
        }
      }

      let jwtForRequest: string | null = null

      if (shouldAttachAuth) {
        jwtForRequest = await waitForJwtToken({ signal: requestSignal })
        if (headers.get('Authorization') !== `Bearer ${jwtForRequest}`) {
          headers.set('Authorization', `Bearer ${jwtForRequest}`)
        }
      }

      // Prepare body
      let body: BodyInit | undefined

      if (config.body !== undefined) {
        if (
          typeof config.body === 'string' ||
          config.body instanceof FormData ||
          config.body instanceof URLSearchParams ||
          config.body instanceof Blob ||
          config.body instanceof ArrayBuffer
        ) {
          body = config.body as BodyInit
        } else {
          body = JSON.stringify(config.body)
        }
      }

      // Destructure custom properties from config
      const {
        params: _params,
        body: _body,
        timeoutMs: _timeoutMs,
        signal: _signal,
        ...restConfig
      } = config

      // Create fetch config
      const fetchConfig: RequestInit = {
        method: config.method || 'GET',
        headers,
        ...(body !== undefined && { body }),
        signal: requestSignal,
        ...restConfig,
      }

      // Log request in debug mode
      if (isDebugEnabled()) {
        const headersForLog = Object.fromEntries(headers.entries())

        if (headersForLog.Authorization) {
          headersForLog.Authorization = '***'
        }

        if (headersForLog.Connection) {
          headersForLog.Connection = '***'
        }

        if (headersForLog['X-Connection-Id']) {
          headersForLog['X-Connection-Id'] = '***'
        }

        if (headersForLog.connectionId) {
          headersForLog.connectionId = '***'
        }

        logger.debug('HTTP Request:', {
          method: config.method || 'GET',
          url,
          headers: headersForLog,
        })
      }

      try {
        // Execute request (TanStack Query handles cancellation via signal)
        let response = await fetch(url, fetchConfig)

        if (
          response.status === 401 &&
          shouldAttachAuth &&
          !config.skipAuthRefresh &&
          !isAuthEndpoint
        ) {
          const refreshed = await refreshJwtToken(jwtForRequest ?? undefined)

          if (refreshed) {
            headers.set('Authorization', `Bearer ${refreshed}`)
            response = await fetch(url, { ...fetchConfig, headers })
          } else {
            // Refresh failed — token is unrecoverable. Force logout.
            triggerForceLogout()
          }
        }

        // Check if response is ok
        if (!response.ok) {
          throw new HttpError(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            response.statusText,
            response.headers,
            response,
            fetchConfig
          )
        }

        // Parse response
        let data: T
        const contentType = response.headers.get('content-type')

        if (contentType?.includes('application/json')) {
          data = await response.json()
        } else {
          data = (await response.text()) as unknown as T
        }

        return data
      } catch (error) {
        // Re-throw HttpError (TanStack Query handles other errors)
        if (error instanceof HttpError) {
          throw error
        }

        // Re-throw abort errors as-is (TanStack Query handles cancellation)
        if (
          error instanceof Error &&
          (error.name === 'AbortError' || error.message.includes('aborted'))
        ) {
          throw error
        }

        // Re-throw network errors as-is (TanStack Query handles retries)
        throw error
      }
    } finally {
      if (timeoutId != null) clearTimeout(timeoutId)
      callerSignal?.removeEventListener('abort', onCallerAbort)
    }
  }

  /**
   * Make HTTP request
   */
  private async request<T>(method: string, path: string, config: RequestConfig = {}): Promise<T> {
    // Build URL
    const url = this.buildURL(path, config.params)

    // Execute request (TanStack Query handles retry logic)
    return await this.executeRequest<T>(url, { ...config, method })
  }

  /**
   * GET request
   */
  async get<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('GET', path, config)
  }

  /**
   * POST request
   */
  async post<TRequest, TResponse>(
    path: string,
    body?: TRequest,
    config?: RequestConfig
  ): Promise<TResponse> {
    return this.request<TResponse>('POST', path, { ...config, body })
  }

  /**
   * PUT request
   */
  async put<TRequest, TResponse>(
    path: string,
    body?: TRequest,
    config?: RequestConfig
  ): Promise<TResponse> {
    return this.request<TResponse>('PUT', path, { ...config, body })
  }

  /**
   * PATCH request
   */
  async patch<TRequest, TResponse>(
    path: string,
    body?: TRequest,
    config?: RequestConfig
  ): Promise<TResponse> {
    return this.request<TResponse>('PATCH', path, { ...config, body })
  }

  /**
   * DELETE request
   */
  async delete<TResponse>(path: string, config?: RequestConfig): Promise<TResponse> {
    return this.request<TResponse>('DELETE', path, config)
  }

  /**
   * Update base URL
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL
  }

  /**
   * Get base URL
   */
  getBaseURL(): string {
    return this.baseURL
  }
}

// Create singleton instance
let httpClientInstance: HttpClient | null = null

/**
 * Get or create HTTP client instance
 */
export function getHttpClient(config?: HttpClientConfig): HttpClient {
  if (!httpClientInstance) {
    httpClientInstance = new HttpClient(config)
  }

  return httpClientInstance
}

// Export default singleton instance
export const httpClient = getHttpClient()
